import { SiteShell } from "@/components/site/SiteShell";

function Bar({ className = "" }: { className?: string }) {
  return (
    <div className={`relative overflow-hidden rounded-lg bg-muted ${className}`}>
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />
    </div>
  );
}

function Card({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-3xl border border-border bg-card p-6 ${className}`}>{children}</div>
  );
}

export default function PricingSkeleton() {
  return (
    <SiteShell>
      <style>{`@keyframes shimmer { 100% { transform: translateX(100%); } }`}</style>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pt-16 pb-10 sm:pt-24">
        <div className="mx-auto max-w-2xl space-y-4 text-center">
          <Bar className="mx-auto h-7 w-44 rounded-full" />
          <Bar className="mx-auto h-12 w-3/4" />
          <Bar className="mx-auto h-12 w-2/3" />
          <Bar className="mx-auto h-4 w-5/6" />
          <Bar className="mx-auto h-4 w-3/4" />
        </div>
      </section>

      {/* Trial band */}
      <section className="mx-auto max-w-6xl px-6 pb-10">
        <div className="rounded-3xl border border-border bg-card p-8 sm:p-10">
          <div className="grid items-center gap-6 sm:grid-cols-[1fr_auto]">
            <div className="space-y-3">
              <Bar className="h-6 w-28 rounded-full" />
              <Bar className="h-7 w-2/3" />
              <Bar className="h-4 w-5/6" />
            </div>
            <Bar className="h-12 w-44 rounded-2xl" />
          </div>
        </div>
      </section>

      {/* Cycle switch */}
      <section className="mx-auto max-w-6xl px-6 pb-6">
        <div className="flex items-center justify-center gap-3">
          <Bar className="h-11 w-48 rounded-2xl" />
          <Bar className="h-7 w-24 rounded-full" />
        </div>
      </section>

      {/* Plans */}
      <section className="mx-auto max-w-7xl px-6 pb-16">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <Bar className="h-12 w-12 rounded-2xl" />
              <Bar className="mt-4 h-6 w-1/3" />
              <Bar className="mt-2 h-3 w-2/3" />
              <Bar className="mt-5 h-9 w-1/2" />
              <div className="my-5 grid grid-cols-2 gap-2 rounded-2xl border border-border bg-muted/40 p-3">
                <Bar className="h-10" />
                <Bar className="h-10" />
              </div>
              <div className="space-y-2.5">
                {Array.from({ length: 6 }).map((_, k) => (
                  <Bar key={k} className="h-3 w-full" />
                ))}
              </div>
              <Bar className="mt-6 h-11 w-full rounded-xl" />
            </Card>
          ))}
        </div>
      </section>

      {/* Common rules */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-5 space-y-3">
              <Bar className="h-10 w-10 rounded-xl" />
              <Bar className="h-4 w-2/3" />
              <Bar className="h-3 w-full" />
            </Card>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="mx-auto max-w-6xl px-6 pb-20">
        <div className="mb-10 space-y-3 text-center">
          <Bar className="mx-auto h-7 w-40 rounded-full" />
          <Bar className="mx-auto h-9 w-1/2" />
          <Bar className="mx-auto h-4 w-1/3" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-5 space-y-3">
              <Bar className="h-11 w-11 rounded-xl" />
              <Bar className="h-4 w-2/3" />
              <Bar className="h-3 w-full" />
              <Bar className="h-3 w-5/6" />
            </Card>
          ))}
        </div>
      </section>

      {/* Compare table */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="mb-8 space-y-3 text-center">
          <Bar className="mx-auto h-9 w-1/3" />
          <Bar className="mx-auto h-4 w-1/4" />
        </div>
        <Card className="p-0 overflow-hidden">
          <div className="border-b border-border bg-muted/40 p-4 grid grid-cols-5 gap-3">
            <Bar className="h-5" />
            {Array.from({ length: 4 }).map((_, i) => (
              <Bar key={i} className="h-5 mx-auto w-20" />
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, r) => (
            <div
              key={r}
              className="grid grid-cols-5 gap-3 p-4 border-b border-border last:border-0"
            >
              <Bar className="h-4" />
              {Array.from({ length: 4 }).map((_, c) => (
                <Bar key={c} className="h-4 mx-auto w-12" />
              ))}
            </div>
          ))}
        </Card>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 pb-24 space-y-3">
        <Bar className="mx-auto mb-6 h-9 w-1/3" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="py-4">
            <Bar className="h-5 w-3/4" />
          </Card>
        ))}
      </section>
    </SiteShell>
  );
}
